import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '@env/environment';

import { removeEmpty } from '../utils';

export interface ApiRequest<T> {
  body?: {};
  id?: number | string;
  ids?: number[] | string[];
  params?: T;
  headers?: HttpHeaders;
  responseType?: any;
  url?: string;
}

export interface ApiParams {
  offset?: number;
  page?: number;
  size?: number;
  sort?: string;
  'sort.dir'?: SortDir;
  'sort.on'?: string;
  name?: string;
  allJobs?: boolean;
  anonymization?: boolean;
  instantSearch?: boolean;
  searchPhrase?: string;
  search?: string;
  assignedUser?: string;
  entityType?: string;
  entityId?: number;
  assign?: string;
}

export enum SortDir {
  DESC = 'desc',
  ASC = 'asc',
  NONE = '',
}

@Injectable()
export class ApiService<T> {
  serviceUrl = '';
  config = environment; // TODO: remove when moved to common-lib
  baseUrl = (this.config && `${this.config.API_BASE_URL}/${this.url}`) || '';
  headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(protected http: HttpClient) {}

  convertOptions<Z>(options: Partial<ApiRequest<Z>>): ApiRequest<HttpParams> {
    const params = new HttpParams({ fromObject: removeEmpty(options.params) });
    return { ...options, params, headers: this.headers };
  }

  get<Z = ApiParams>({ id, url, ...options }: Partial<ApiRequest<Z>> = {}): Observable<any> {
    return this.http
      .get(this.buildUrl({ id, url }), this.convertOptions<Z>(options))
      .pipe(catchError(this.handleError));
  }

  post<K = {}>({ id, url, body, ...options }: Partial<ApiRequest<ApiParams>>): Observable<K> {
    return this.http
      .post<K>(this.buildUrl({ id, url }), JSON.stringify(body), {
        ...this.convertOptions(options),
      })
      .pipe(catchError(this.handleError));
  }

  put({ id, url, body, ...options }: Partial<ApiRequest<ApiParams>>): Observable<{}> {
    return this.http
      .put(this.buildUrl({ id, url }), JSON.stringify(body), this.convertOptions(options))
      .pipe(catchError(this.handleError));
  }

  patch<K = {}>({ id, url, body, ...options }: Partial<ApiRequest<ApiParams>>): Observable<K> {
    return this.http
      .patch<K>(this.buildUrl({ id, url }), JSON.stringify(body), this.convertOptions(options))
      .pipe(catchError(this.handleError));
  }

  delete({ id, url }: Partial<ApiRequest<ApiParams>>): Observable<{}> {
    return this.http.delete(this.buildUrl({ id, url })).pipe(catchError(this.handleError));
  }

  deleteMultiple({ id, ids, url }: Partial<ApiRequest<ApiParams>>): Observable<{}> {
    let queryParams = new HttpParams();
    if (!!id) {
      queryParams = queryParams.append('id', id);
    }
    queryParams = queryParams.appendAll({ ids });
    const buildUrl = this.buildUrl({ url });
    return this.http
      .delete(buildUrl + '?' + queryParams.toString())
      .pipe(catchError(this.handleError));
  }

  handleError(error: Response): Observable<never> {
    return throwError(error);
  }

  private buildUrl({ id, url = '' }: Partial<ApiRequest<ApiParams>>): string {
    return `${this.baseUrl}${url}${id || ''}`;
  }

  set url(url: string) {
    if (!!url) {
      this.serviceUrl = url;
      this.baseUrl = `${this.baseUrl}${this.url}`;
    }
  }

  get url() {
    return this.serviceUrl;
  }
}
